import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import { Box, Button, Chip, Grid, IconButton, Paper, Skeleton, Stack, Switch, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import { useConfirm } from 'material-ui-confirm';
import { useContext, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import ErrorBoundary from '../../../components/common/ErrorBoundary';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import SextforceAutoRepostColorsDialog from '../../../components/services/sextforce/autoRepost/SextforceAutoRepostColorsDialog';
import SextforceAutoStoriesCalendar from '../../../components/services/sextforce/autoStories/SextforceAutoStoriesCalendar';
import SextforceAutoStoriesScheduleDialog from '../../../components/services/sextforce/autoStories/SextforceAutoStoriesScheduleDialog';
import SextforceCaptionsDialog from '../../../components/services/sextforce/SextforceCaptionsDialog';
import useSextforceAutoRepostColors from '../../../hooks/useSextforceAutoRepostColors';
import useSextforceAutoStories, { AutoStorySchedule, AutoStorySchedulePayload, AutoStoryLog } from '../../../hooks/useSextforceAutoStories';
import useSubscriber from '../../../hooks/useSubscriber';
import { SettingsContext } from '../../../store/SettingsContext';

// Converts a persisted AutoStorySchedule (UPPERCASE mode/repeatUnit as returned by GET) back
// into the payload shape (lowercase) required by POST/PUT, applying any field overrides.
const scheduleToPayload = (schedule: AutoStorySchedule, overrides: Partial<AutoStorySchedulePayload> = {}): AutoStorySchedulePayload => ({
    name: schedule.name,
    mode: schedule.mode === 'MANUAL' ? 'manual' : 'album',
    colorId: schedule.colorId,
    startDate: schedule.startDate,
    repeatAmount: schedule.repeatAmount,
    repeatUnit: schedule.repeatUnit ? (schedule.repeatUnit === 'HOURS' ? 'hours' : 'days') : null,
    deleteAfterHours: schedule.deleteAfterHours,
    active: schedule.active,
    albumId: schedule.albumId,
    postImage: schedule.postImage,
    postVideo: schedule.postVideo,
    postGif: schedule.postGif,
    captionCategory: schedule.captionCategory,
    vaultMediaId: schedule.vaultMediaId,
    canvasWidth: schedule.canvasWidth,
    canvasHeight: schedule.canvasHeight,
    crop: schedule.crop,
    texts: schedule.texts,
    ...overrides,
});

const repeatText = (schedule: AutoStorySchedule): string => {
    if (!schedule.repeatAmount || !schedule.repeatUnit) {
        return 'One-off';
    }

    const unit = schedule.repeatUnit.toLowerCase();

    return `Every ${schedule.repeatAmount} ${unit}`;
};

const autoDeleteText = (schedule: AutoStorySchedule): string =>
    schedule.deleteAfterHours !== null ? `After ${schedule.deleteAfterHours}h` : '—';

const SextforceAutoStories = () => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const confirm = useConfirm();
    const { enqueueSnackbar } = useSnackbar();

    const { data: subscriber, isLoading: isSubscriberLoading, setServiceRunning, setServiceRunningLoading } = useSubscriber();

    const { schedules, isLoadingSchedules, updateSchedule, deleteSchedule, useLogs } = useSextforceAutoStories();
    const { data: colors } = useSextforceAutoRepostColors();

    const [showColorsDialog, setShowColorsDialog] = useState(false);
    const [showCaptionsDialog, setShowCaptionsDialog] = useState(false);
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState<AutoStorySchedule | null>(null);

    const recentLogsRangeStart = useMemo(() => moment().subtract(7, 'days'), []);
    const recentLogsRangeEnd = useMemo(() => moment().add(1, 'minute'), []);
    const { data: recentLogs } = useLogs(recentLogsRangeStart, recentLogsRangeEnd);

    const latestLogByScheduleId = useMemo(() => {
        const map: Record<string, AutoStoryLog> = {};

        (recentLogs || []).forEach(log => {
            const existing = map[log.scheduleId];

            if (!existing || moment(log.postedAt).isAfter(moment(existing.postedAt))) {
                map[log.scheduleId] = log;
            }
        });

        return map;
    }, [recentLogs]);

    // Undefined means the toggle has never been set — the scheduler treats that as running
    const serviceActive: boolean = !!(subscriber && subscriber.sextforce) && subscriber.sextforce.autoStories?.active !== false;

    const resolveColor = (colorId: string | null): { name: string; hex: string } => {
        if (!colorId || !colors) {
            return { name: 'No category', hex: theme.palette.grey[400] };
        }

        const found = colors.find(c => c._id === colorId);

        return found ? { name: found.name, hex: found.color } : { name: 'No category', hex: theme.palette.grey[400] };
    };

    const handleOpenNewSchedule = () => {
        setEditingSchedule(null);
        setShowScheduleDialog(true);
    };

    const handleOpenEditSchedule = (schedule: AutoStorySchedule) => {
        setEditingSchedule(schedule);
        setShowScheduleDialog(true);
    };

    const handleToggleActive = (schedule: AutoStorySchedule, active: boolean) => {
        updateSchedule(schedule.id, scheduleToPayload(schedule, { active }));
    };

    const handleDelete = (schedule: AutoStorySchedule) => {
        confirm({
            title: 'Delete Scheduled Story',
            description: `Are you sure you want to delete "${schedule.name}"? This cannot be undone.`,
            confirmationText: 'Delete',
        })
            .then(() => {
                deleteSchedule(schedule.id).then(result => {
                    if (result.success) {
                        enqueueSnackbar('Scheduled story deleted.', { variant: 'success' });
                    }
                });
            })
            .catch(() => {});
    };

    return (
        <PageContainer>
            <ErrorBoundary>
                {subscriber && '_id' in subscriber && subscriber._id && (
                    <>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}`} />{' '}
                            Auto Stories for {subscriber.username}
                        </Typography>

                        <StyledCard noCard noBottomMargin>
                            <StyledCardTitleBar title="Auto Stories" />
                        </StyledCard>

                        <StyledCard>
                            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                                <Grid item xs>
                                    <span>Auto Stories is currently </span>
                                    {serviceActive ? (
                                        <span style={{ color: theme.palette.success.dark }}>running</span>
                                    ) : (
                                        <span style={{ color: theme.palette.error.main }}>not running</span>
                                    )}
                                    <span>.</span>
                                </Grid>
                                <Grid item xs="auto">
                                    {isSubscriberLoading || setServiceRunningLoading ? (
                                        <Skeleton width={'115px'} height={'51px'} />
                                    ) : (
                                        <LargeMaterialUISwitch
                                            checked={serviceActive}
                                            onChange={e => setServiceRunning('sextforce.autoStories', e.target.checked)}
                                            color="primary"
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </StyledCard>

                        <StyledCard noCard>
                            <Button variant="contained" color="secondary" fullWidth size="large" startIcon={<AddIcon />} onClick={handleOpenNewSchedule}>
                                New Scheduled Story
                            </Button>
                        </StyledCard>
                        <StyledCard noCard>
                            <Button variant="contained" color="info" fullWidth size="large" onClick={() => setShowColorsDialog(true)}>
                                Colour Categories
                            </Button>
                        </StyledCard>
                        <StyledCard noCard>
                            <Button
                                variant="contained"
                                color="info"
                                fullWidth
                                size="large"
                                startIcon={<SubtitlesIcon />}
                                onClick={() => setShowCaptionsDialog(true)}
                            >
                                Manage Captions
                            </Button>
                        </StyledCard>

                        <SextforceAutoRepostColorsDialog open={showColorsDialog} onClose={() => setShowColorsDialog(false)} />
                        <SextforceCaptionsDialog open={showCaptionsDialog} onClose={() => setShowCaptionsDialog(false)} />
                        <SextforceAutoStoriesScheduleDialog
                            open={showScheduleDialog}
                            schedule={editingSchedule}
                            onClose={() => setShowScheduleDialog(false)}
                        />

                        <StyledCard noCard noBottomMargin>
                            <StyledCardTitleBar
                                title={`Scheduled Stories (${schedules ? schedules.length.toLocaleString() : 0})`}
                                isLoading={isLoadingSchedules}
                            />
                        </StyledCard>

                        <StyledCard>
                            <Stack spacing={1}>
                                {!isLoadingSchedules && schedules && schedules.length === 0 && (
                                    <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 2 }}>
                                        No scheduled stories yet. Click "New Scheduled Story" above to add one.
                                    </Typography>
                                )}

                                {schedules?.map(schedule => {
                                    const { name: colorName, hex: colorHex } = resolveColor(schedule.colorId);
                                    const latestLog = latestLogByScheduleId[schedule.id];

                                    return (
                                        <Paper key={schedule.id} variant="outlined" sx={{ p: 1.5 }}>
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid item xs={12} sm={3}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Tooltip title={colorName}>
                                                            <Box
                                                                sx={{
                                                                    width: 10,
                                                                    height: 10,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: colorHex,
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        </Tooltip>
                                                        <Typography variant="body2" fontWeight="bold" noWrap>
                                                            {schedule.name}
                                                        </Typography>
                                                    </Stack>
                                                    <Chip
                                                        size="small"
                                                        label={schedule.mode === 'MANUAL' ? 'Manual' : 'Album'}
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Next post
                                                    </Typography>
                                                    <Typography variant="body2">{moment(schedule.nextPostDate).format('lll')}</Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Repeat
                                                    </Typography>
                                                    <Typography variant="body2">{repeatText(schedule)}</Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={1}>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Auto-delete
                                                    </Typography>
                                                    <Typography variant="body2">{autoDeleteText(schedule)}</Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={1}>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Last result
                                                    </Typography>
                                                    {latestLog ? (
                                                        latestLog.status === 'failed' ? (
                                                            <Tooltip title={latestLog.error || 'Failed'}>
                                                                <CancelIcon color="error" fontSize="small" />
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title={`Posted ${moment(latestLog.postedAt).fromNow()}`}>
                                                                <CheckCircleIcon color="success" fontSize="small" />
                                                            </Tooltip>
                                                        )
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            —
                                                        </Typography>
                                                    )}
                                                </Grid>
                                                <Grid item xs={6} sm={1}>
                                                    <Switch
                                                        checked={schedule.active}
                                                        onChange={e => handleToggleActive(schedule, e.target.checked)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                                    <IconButton size="small" onClick={() => handleOpenEditSchedule(schedule)}>
                                                        <EditIcon fontSize="small" color="primary" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => handleDelete(schedule)}>
                                                        <DeleteIcon fontSize="small" color="error" />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        </StyledCard>

                        <StyledCard noCard noBottomMargin>
                            <StyledCardTitleBar title="Calendar" />
                        </StyledCard>
                        <StyledCard noCard>
                            <Paper>
                                <SextforceAutoStoriesCalendar schedules={schedules || []} onEditSchedule={handleOpenEditSchedule} />
                            </Paper>
                        </StyledCard>
                    </>
                )}
            </ErrorBoundary>
        </PageContainer>
    );
};

export default SextforceAutoStories;
